import {
  isValidEmailAddress,
  isValidPhoneNumber,
} from '../../shared/contactValidation';

export type ApplicationsIntakeStep =
  | 'Students'
  | 'Parents'
  | 'Extended Family'
  | 'Documents'
  | 'Review'
  | 'Schedule';

export interface ApplicationsIntakeStudentLike {
  firstName: string;
  lastName: string;
  grade: string;
  currentGrade: string;
}

export interface ApplicationsIntakeParentLike {
  name: string;
  cellPhone: string;
  email: string;
  isDeceasedOrNoLongerLegalGuardian?: boolean;
}

export interface ApplicationsIntakeFamilyLike {
  surname: string;
  homePhone: string;
  address: string;
  city: string;
  state: string;
  zip: string;
  father: ApplicationsIntakeParentLike;
  mother: ApplicationsIntakeParentLike;
}

export interface ApplicationsIntakeRequiredDocumentUploadLike {
  key: string;
  label: string;
  fileName?: string | null;
}

export interface ApplicationsIntakeDocumentsLike {
  fatherPolicyAware: boolean;
  motherPolicyAware: boolean;
  requirementsReady?: boolean;
  policyAcknowledgementRequired?: boolean;
  requiredUploads?: ApplicationsIntakeRequiredDocumentUploadLike[];
}

export interface ApplicationsIntakeScheduleFormLike {
  name: string;
  email: string;
  phone: string;
  notes?: string;
}

export interface ApplicationsIntakeValidationInput {
  step: ApplicationsIntakeStep;
  students: ApplicationsIntakeStudentLike[];
  family: ApplicationsIntakeFamilyLike;
  documents: ApplicationsIntakeDocumentsLike;
  consentChecked: boolean;
  certificationRequired?: boolean;
  gradeDiscrepancyAcknowledged?: boolean;
  scheduleForm: ApplicationsIntakeScheduleFormLike;
  selectedDate: number | null;
  selectedTime: string | null;
  interviewSlotsByStudentId: Record<string, number>;
  schedulableStudentIds: string[];
}

export interface ApplicationsIntakeActionState {
  canAdvanceStep: boolean;
  advanceIssues: ApplicationsIntakeAdvanceIssue[];
  canScheduleInterview: boolean;
  canSubmitWithoutScheduling: boolean;
  canFinishScheduledSubmission: boolean;
  gradeDiscrepancyWarnings: ApplicationsIntakeGradeDiscrepancyWarning[];
}

export type ApplicationsIntakeAdvanceIssueKind = 'missing' | 'invalid';

export interface ApplicationsIntakeAdvanceIssue {
  key: string;
  label: string;
  kind: ApplicationsIntakeAdvanceIssueKind;
}

export interface ApplicationsIntakeGradeDiscrepancyWarning {
  key: string;
  studentLabel: string;
  applyingGrade: string;
  currentGrade: string;
  label: string;
}

export const shouldShowFooterNavigation = (
  step: ApplicationsIntakeStep | null,
  scheduleView: 'calendar' | 'form' | 'success',
): boolean => step != null && !(step === 'Schedule' && scheduleView === 'success');

const readText = (value: unknown): string => String(value ?? '').trim();

const hasFiveDigitZip = (value: unknown): boolean =>
  readText(value).replace(/[^\d]/g, '').length >= 5;

const hasAnyText = (values: unknown[]): boolean =>
  values.some((value) => Boolean(readText(value)));

const createAdvanceIssue = (
  key: string,
  label: string,
  kind: ApplicationsIntakeAdvanceIssueKind = 'missing',
): ApplicationsIntakeAdvanceIssue => ({
  key,
  label,
  kind,
});

const withStudentIssueLabel = (index: number, label: string): string =>
  index > 0 ? `Student ${index + 1}: ${label}` : label;

const getMissingRequiredDocumentUploads = (
  documents: ApplicationsIntakeDocumentsLike,
): ApplicationsIntakeRequiredDocumentUploadLike[] =>
  (documents.requiredUploads ?? []).filter((upload) => !readText(upload.fileName));

const normalizeGradeText = (value: unknown): string =>
  readText(value)
    .toLowerCase()
    .replace(/&/g, ' and ')
    .replace(/[^a-z0-9]+/g, ' ')
    .replace(/\s+/g, ' ')
    .trim();

const gradeWordRanks: Record<string, number> = {
  first: 1,
  second: 2,
  third: 3,
  fourth: 4,
  fifth: 5,
  sixth: 6,
  seventh: 7,
  eighth: 8,
  ninth: 9,
  tenth: 10,
  eleventh: 11,
  twelfth: 12,
};

export const getApplicationsIntakeGradeRank = (value: unknown): number | null => {
  const normalized = normalizeGradeText(value);
  if (!normalized) return null;

  const compact = normalized.replace(/\s+/g, '');
  if (normalized.includes('nursery')) return -1;
  if (normalized.includes('kindergarten') || normalized === 'k' || normalized === 'kg') return 0;
  if (compact.includes('pre1a')) return 0.5;

  const numericGrade = normalized.match(/(?:^|\b)(?:grade|gr|g)?\s*(\d{1,2})(?:st|nd|rd|th)?(?:\b|$)/);
  if (numericGrade) {
    const rank = Number(numericGrade[1]);
    if (Number.isFinite(rank) && rank >= 1 && rank <= 12) return rank;
  }

  for (const [word, rank] of Object.entries(gradeWordRanks)) {
    if (new RegExp(`\\b${word}\\b`).test(normalized)) return rank;
  }

  return null;
};

export const getApplicationsIntakeGradeDiscrepancyWarnings = (
  students: ApplicationsIntakeStudentLike[],
): ApplicationsIntakeGradeDiscrepancyWarning[] => {
  const warnings: ApplicationsIntakeGradeDiscrepancyWarning[] = [];

  students.forEach((student, index) => {
    const applyingGrade = readText(student.grade);
    const currentGrade = readText(student.currentGrade);
    const applyingRank = getApplicationsIntakeGradeRank(applyingGrade);
    const currentRank = getApplicationsIntakeGradeRank(currentGrade);

    if (applyingRank == null || currentRank == null) return;
    if (Math.abs(applyingRank - currentRank) <= 1) return;

    const studentName = readText(`${student.firstName} ${student.lastName}`);
    const studentLabel = studentName || `Student ${index + 1}`;
    warnings.push({
      key: `review.gradeDiscrepancy.${index}`,
      studentLabel,
      applyingGrade,
      currentGrade,
      label: `${studentLabel}: applying for ${applyingGrade} while current grade is ${currentGrade}`,
    });
  });

  return warnings;
};

export const isStudentsStepReady = (students: ApplicationsIntakeStudentLike[]): boolean =>
  students.length > 0
  && students.every((student) =>
    Boolean(
      readText(student.firstName)
      && readText(student.lastName)
      && readText(student.grade)
      && readText(student.currentGrade),
    ),
  );

export const hasParentContactName = (family: ApplicationsIntakeFamilyLike): boolean =>
  Boolean(readText(family.father.name) || readText(family.mother.name));

export const isParentsStepReady = (family: ApplicationsIntakeFamilyLike): boolean => {
  const hasRequiredAddress =
    Boolean(readText(family.surname))
    && Boolean(readText(family.address))
    && Boolean(readText(family.city))
    && Boolean(readText(family.state))
    && hasFiveDigitZip(family.zip);

  const hasAnyPhone =
    isValidPhoneNumber(family.homePhone)
    || isValidPhoneNumber(family.father.cellPhone)
    || isValidPhoneNumber(family.mother.cellPhone);

  const hasAnyParentEmail =
    isValidEmailAddress(family.father.email)
    || isValidEmailAddress(family.mother.email);

  return hasRequiredAddress && hasParentContactName(family) && hasAnyPhone && hasAnyParentEmail;
};

const isFatherAcknowledgementRequired = (family: ApplicationsIntakeFamilyLike): boolean =>
  family.father.isDeceasedOrNoLongerLegalGuardian !== true;

const isMotherAcknowledgementRequired = (family: ApplicationsIntakeFamilyLike): boolean =>
  family.mother.isDeceasedOrNoLongerLegalGuardian !== true;

const isPolicyAcknowledgementRequired = (
  documents: ApplicationsIntakeDocumentsLike,
): boolean => documents.policyAcknowledgementRequired !== false;

const areDocumentRequirementsReady = (
  documents: ApplicationsIntakeDocumentsLike,
): boolean => documents.requirementsReady !== false;

export const isDocumentsStepReady = (
  documents: ApplicationsIntakeDocumentsLike,
  family: ApplicationsIntakeFamilyLike,
): boolean =>
  areDocumentRequirementsReady(documents)
  && (
    !isPolicyAcknowledgementRequired(documents)
    || !isFatherAcknowledgementRequired(family)
    || documents.fatherPolicyAware
  )
  && (
    !isPolicyAcknowledgementRequired(documents)
    || !isMotherAcknowledgementRequired(family)
    || documents.motherPolicyAware
  )
  && getMissingRequiredDocumentUploads(documents).length === 0;

export const isReviewStepReady = (
  consentChecked: boolean,
  gradeDiscrepancyWarnings: ApplicationsIntakeGradeDiscrepancyWarning[] = [],
  gradeDiscrepancyAcknowledged = false,
  certificationRequired = true,
): boolean =>
  (!certificationRequired || consentChecked)
  && (gradeDiscrepancyWarnings.length === 0 || gradeDiscrepancyAcknowledged);

export const isScheduleFormReady = (scheduleForm: ApplicationsIntakeScheduleFormLike): boolean =>
  Boolean(readText(scheduleForm.name))
  && isValidEmailAddress(scheduleForm.email)
  && isValidPhoneNumber(scheduleForm.phone);

export const hasSelectedScheduleSlot = (selectedDate: number | null, selectedTime: string | null): boolean =>
  selectedDate != null && Boolean(readText(selectedTime));

export const hasScheduledInterviewForAllStudents = (
  schedulableStudentIds: string[],
  interviewSlotsByStudentId: Record<string, number>,
): boolean =>
  schedulableStudentIds.length > 0
  && schedulableStudentIds.every((studentId) => Number.isFinite(interviewSlotsByStudentId[studentId]));

export const getStudentsAdvanceIssues = (
  students: ApplicationsIntakeStudentLike[],
): ApplicationsIntakeAdvanceIssue[] => {
  if (students.length === 0) {
    return [
      createAdvanceIssue(
        'students.__count__',
        'Add at least one student applicant',
      ),
    ];
  }

  const issues: ApplicationsIntakeAdvanceIssue[] = [];
  students.forEach((student, index) => {
    if (!readText(student.firstName)) {
      issues.push(
        createAdvanceIssue(
          `students.${index}.firstName`,
          withStudentIssueLabel(index, 'First Name'),
        ),
      );
    }
    if (!readText(student.lastName)) {
      issues.push(
        createAdvanceIssue(
          `students.${index}.lastName`,
          withStudentIssueLabel(index, 'Last Name'),
        ),
      );
    }
    if (!readText(student.grade)) {
      issues.push(
        createAdvanceIssue(
          `students.${index}.grade`,
          withStudentIssueLabel(index, 'Applying for Grade'),
        ),
      );
    }
    if (!readText(student.currentGrade)) {
      issues.push(
        createAdvanceIssue(
          `students.${index}.currentGrade`,
          withStudentIssueLabel(index, 'Current Grade'),
        ),
      );
    }
  });
  return issues;
};

export const getParentsAdvanceIssues = (
  family: ApplicationsIntakeFamilyLike,
): ApplicationsIntakeAdvanceIssue[] => {
  const issues: ApplicationsIntakeAdvanceIssue[] = [];

  if (!readText(family.surname)) {
    issues.push(createAdvanceIssue('family.surname', 'Family Surname'));
  }
  if (!readText(family.address)) {
    issues.push(createAdvanceIssue('family.address', 'Home Address'));
  }
  if (!readText(family.city)) {
    issues.push(createAdvanceIssue('family.city', 'City'));
  }
  if (!readText(family.state)) {
    issues.push(createAdvanceIssue('family.state', 'State'));
  }

  const zipHasValue = Boolean(readText(family.zip));
  if (!hasFiveDigitZip(family.zip)) {
    issues.push(
      createAdvanceIssue(
        'family.zip',
        zipHasValue ? 'Zip must contain at least 5 digits' : 'Zip',
        zipHasValue ? 'invalid' : 'missing',
      ),
    );
  }

  if (!hasParentContactName(family)) {
    issues.push(
      createAdvanceIssue(
        'parents.group.parentName',
        'At least one parent name',
      ),
    );
  }

  const phoneValues = [
    family.homePhone,
    family.father.cellPhone,
    family.mother.cellPhone,
  ];
  const hasAnyPhone = phoneValues.some((value) => isValidPhoneNumber(value));
  if (!hasAnyPhone) {
    issues.push(
      createAdvanceIssue(
        'parents.group.phone',
        hasAnyText(phoneValues)
          ? 'At least one valid working phone number'
          : 'At least one working phone number',
        hasAnyText(phoneValues) ? 'invalid' : 'missing',
      ),
    );
  }

  const emailValues = [family.father.email, family.mother.email];
  const hasAnyParentEmail = emailValues.some((value) =>
    isValidEmailAddress(value),
  );
  if (!hasAnyParentEmail) {
    issues.push(
      createAdvanceIssue(
        'parents.group.email',
        hasAnyText(emailValues)
          ? 'At least one valid parent email address'
          : 'At least one parent email address',
        hasAnyText(emailValues) ? 'invalid' : 'missing',
      ),
    );
  }

  return issues;
};

export const getDocumentsAdvanceIssues = (
  documents: ApplicationsIntakeDocumentsLike,
  family: ApplicationsIntakeFamilyLike,
): ApplicationsIntakeAdvanceIssue[] => {
  const issues: ApplicationsIntakeAdvanceIssue[] = [];
  if (!areDocumentRequirementsReady(documents)) {
    issues.push(
      createAdvanceIssue(
        'documents.requirementsReady',
        'Application requirements',
      ),
    );
    return issues;
  }

  const requirePolicyAcknowledgement = isPolicyAcknowledgementRequired(documents);
  if (requirePolicyAcknowledgement && isFatherAcknowledgementRequired(family) && !documents.fatherPolicyAware) {
    issues.push(
      createAdvanceIssue(
        'documents.fatherPolicyAware',
        'Father acknowledgement',
      ),
    );
  }
  if (requirePolicyAcknowledgement && isMotherAcknowledgementRequired(family) && !documents.motherPolicyAware) {
    issues.push(
      createAdvanceIssue(
        'documents.motherPolicyAware',
        'Mother acknowledgement',
      ),
    );
  }
  getMissingRequiredDocumentUploads(documents).forEach((upload) => {
    issues.push(
      createAdvanceIssue(
        `documents.requiredUploads.${upload.key}`,
        upload.label,
      ),
    );
  });
  return issues;
};

export const getApplicationsIntakeSubmissionIssues = (
  input: ApplicationsIntakeValidationInput,
): ApplicationsIntakeAdvanceIssue[] => {
  const gradeDiscrepancyWarnings = getApplicationsIntakeGradeDiscrepancyWarnings(input.students);
  return [
    ...getStudentsAdvanceIssues(input.students),
    ...getParentsAdvanceIssues(input.family),
    ...getDocumentsAdvanceIssues(input.documents, input.family),
    ...getReviewAdvanceIssues(
      input.consentChecked,
      gradeDiscrepancyWarnings,
      input.gradeDiscrepancyAcknowledged === true,
      input.certificationRequired !== false,
    ),
  ];
};

export const getReviewAdvanceIssues = (
  consentChecked: boolean,
  gradeDiscrepancyWarnings: ApplicationsIntakeGradeDiscrepancyWarning[] = [],
  gradeDiscrepancyAcknowledged = false,
  certificationRequired = true,
): ApplicationsIntakeAdvanceIssue[] => {
  const issues: ApplicationsIntakeAdvanceIssue[] = [];
  if (certificationRequired && !consentChecked) {
    issues.push(createAdvanceIssue('review.consentChecked', 'Certification'));
  }
  if (gradeDiscrepancyWarnings.length > 0 && !gradeDiscrepancyAcknowledged) {
    issues.push(
      createAdvanceIssue(
        'review.gradeDiscrepancyAcknowledged',
        'Grade discrepancy review',
      ),
    );
  }
  return issues;
};

export const getScheduleAdvanceIssues = (
  scheduleForm: ApplicationsIntakeScheduleFormLike,
  selectedDate: number | null,
  selectedTime: string | null,
): ApplicationsIntakeAdvanceIssue[] => {
  const issues: ApplicationsIntakeAdvanceIssue[] = [];
  if (selectedDate == null || !readText(selectedTime)) {
    issues.push(createAdvanceIssue('schedule.slot', 'Interview date and time'));
  }
  if (!readText(scheduleForm.name)) {
    issues.push(createAdvanceIssue('scheduleForm.name', 'Name'));
  }
  const emailHasValue = Boolean(readText(scheduleForm.email));
  if (!isValidEmailAddress(scheduleForm.email)) {
    issues.push(
      createAdvanceIssue(
        'scheduleForm.email',
        emailHasValue ? 'Valid email address' : 'Email',
        emailHasValue ? 'invalid' : 'missing',
      ),
    );
  }
  const phoneHasValue = Boolean(readText(scheduleForm.phone));
  if (!isValidPhoneNumber(scheduleForm.phone)) {
    issues.push(
      createAdvanceIssue(
        'scheduleForm.phone',
        phoneHasValue ? 'Valid phone number' : 'Phone',
        phoneHasValue ? 'invalid' : 'missing',
      ),
    );
  }
  return issues;
};

export const getApplicationsIntakeActionState = (
  input: ApplicationsIntakeValidationInput,
): ApplicationsIntakeActionState => {
  const studentsReady = isStudentsStepReady(input.students);
  const parentsReady = isParentsStepReady(input.family);
  const documentsReady = isDocumentsStepReady(input.documents, input.family);
  const gradeDiscrepancyWarnings = getApplicationsIntakeGradeDiscrepancyWarnings(input.students);
  const gradeDiscrepancyAcknowledged = input.gradeDiscrepancyAcknowledged === true;
  const reviewReady = isReviewStepReady(
    input.consentChecked,
    gradeDiscrepancyWarnings,
    gradeDiscrepancyAcknowledged,
    input.certificationRequired !== false,
  );
  const scheduleFormReady = isScheduleFormReady(input.scheduleForm);
  const scheduleSlotReady = hasSelectedScheduleSlot(input.selectedDate, input.selectedTime);
  const scheduledForAllStudents = hasScheduledInterviewForAllStudents(
    input.schedulableStudentIds,
    input.interviewSlotsByStudentId,
  );
  const baseSubmissionReady = studentsReady && parentsReady && documentsReady && reviewReady;
  const scheduleReady = baseSubmissionReady
    && scheduleFormReady
    && scheduleSlotReady
    && input.schedulableStudentIds.length > 0;

  let canAdvanceStep = false;
  let advanceIssues: ApplicationsIntakeAdvanceIssue[] = [];
  switch (input.step) {
    case 'Students':
      canAdvanceStep = studentsReady;
      advanceIssues = getStudentsAdvanceIssues(input.students);
      break;
    case 'Parents':
      canAdvanceStep = parentsReady;
      advanceIssues = getParentsAdvanceIssues(input.family);
      break;
    case 'Extended Family':
      canAdvanceStep = true;
      advanceIssues = [];
      break;
    case 'Documents':
      canAdvanceStep = documentsReady;
      advanceIssues = getDocumentsAdvanceIssues(input.documents, input.family);
      break;
    case 'Review':
      canAdvanceStep = reviewReady;
      advanceIssues = getReviewAdvanceIssues(
        input.consentChecked,
        gradeDiscrepancyWarnings,
        gradeDiscrepancyAcknowledged,
        input.certificationRequired !== false,
      );
      break;
    case 'Schedule':
      canAdvanceStep = false;
      advanceIssues = getScheduleAdvanceIssues(
        input.scheduleForm,
        input.selectedDate,
        input.selectedTime,
      );
      break;
  }

  return {
    canAdvanceStep,
    advanceIssues,
    canScheduleInterview: scheduleReady,
    canSubmitWithoutScheduling: baseSubmissionReady,
    canFinishScheduledSubmission: baseSubmissionReady && scheduledForAllStudents && scheduleFormReady,
    gradeDiscrepancyWarnings,
  };
};
